import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { NavDescriptor, TwistRouteData } from './nav-descriptor';
import { NavRegistry } from './nav-registry';

/**
 * Resolves the navigation "active trail" for a given route.
 *
 * A deep/detail route declares its owning navigation node via the
 * typed `navId` field in {@link TwistRouteData}. The resolver walks
 * the activated-route chain (deepest → root), reads the nearest
 * `navId`, and maps it to a {@link NavDescriptor} via the
 * {@link NavRegistry}. This lets a detail page (e.g.
 * `/devices/:id/telemetry`) light up its owning sidebar/tab node.
 */
@Injectable({ providedIn: 'root' })
export class ActiveTrailResolver {
  private readonly _registry = inject(NavRegistry);

  /**
   * Returns the nearest `navId` declared on the route chain, walking
   * from the given (deepest) snapshot up towards the root. Returns
   * `undefined` when no route in the chain declares one.
   */
  public navIdFor(snapshot: ActivatedRouteSnapshot | null): string | undefined {
    // Callers pass the deepest activated snapshot; we walk up the
    // parent chain (deep → root) and return the first declared navId.
    let current: ActivatedRouteSnapshot | null = snapshot;
    while (current) {
      const data = current.data as TwistRouteData;
      if (data?.navId) {
        return data.navId;
      }
      current = current.parent;
    }
    return undefined;
  }

  /**
   * Resolves the owning {@link NavDescriptor} for the given route,
   * or `undefined` when the route declares no `navId` or the id has
   * no matching descriptor in the registry.
   */
  public descriptorFor(snapshot: ActivatedRouteSnapshot | null): NavDescriptor | undefined {
    const navId = this.navIdFor(snapshot);
    return navId ? this._registry.descriptor(navId) : undefined;
  }
}
